import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronUp, ChevronDown } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { brl, discountPct } from "@/lib/format";
import { Card, Field, TextArea, SaveBtn, Toggle, Empty } from "./ui";
import { SmartImage } from "../public/media";
import Uploader from "../common/Uploader";

const TABS = [["stories", "Stories"], ["videos", "Menu em Vídeo"], ["best", "Mais Vendidos"], ["offers", "Ofertas"]];

export default function ContentModule(props) {
  const [tab, setTab] = useState("stories");
  return (
    <div data-testid="content-module">
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`content-tab-${k}`} className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-[#FF6B00] text-white" : "bg-white/5 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      {tab === "stories" && <Stories {...props} />}
      {tab === "videos" && <Videos {...props} />}
      {tab === "best" && <BestSellers {...props} />}
      {tab === "offers" && <Offers {...props} />}
    </div>
  );
}

// ---------- Stories ----------
function Stories({ rid, data, reload }) {
  const [ed, setEd] = useState(null);
  const del = async (s) => { if (!window.confirm("Excluir story?")) return; try { await api.delete(`/restaurants/${rid}/stories/${s.id}`); reload(); toast.success("Excluído"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setEd({ new: true })} data-testid="story-add" className="flex items-center gap-2 rounded-full bg-[#FF6B00] px-4 py-2.5 font-bold orange-glow"><Plus size={18} /> Novo story</button></div>
      {data.stories.length === 0 && <Empty>Nenhum story ainda.</Empty>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {data.stories.map((s) => (
          <Card key={s.id} className="!p-0 overflow-hidden">
            <div className="relative h-40"><SmartImage src={s.thumbnail || s.media} className="h-full w-full" />
              {!s.active && <span className="absolute top-2 left-2 text-[10px] bg-black/70 px-2 py-0.5 rounded">Inativo</span>}
            </div>
            <div className="p-3">
              <p className="font-semibold text-sm truncate">{s.title || "Sem título"}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEd(s)} data-testid={`story-edit-${s.id}`} className="flex-1 text-xs font-semibold bg-white/10 py-1.5 rounded-lg flex items-center justify-center gap-1"><Pencil size={13} /> Editar</button>
                <button onClick={() => del(s)} data-testid={`story-del-${s.id}`} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-rose-600 flex items-center justify-center"><Trash2 size={14} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {ed && <StoryEditor rid={rid} story={ed.new ? null : ed} data={data} onClose={() => setEd(null)} onSaved={() => { setEd(null); reload(); }} />}
    </div>
  );
}
function StoryEditor({ rid, story, data, onClose, onSaved }) {
  const [f, setF] = useState(story || { media_type: "image", media: null, thumbnail: null, title: "", text: "", order: data.stories.length, active: true, start_at: null, expire_at: null, cta_type: "nenhum", cta_value: null });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      if (story) await api.put(`/restaurants/${rid}/stories/${story.id}`, payload);
      else await api.post(`/restaurants/${rid}/stories`, payload);
      toast.success("Story salvo"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  const needsProduct = ["ver_produto", "adicionar"].includes(f.cta_type);
  const needsCategory = f.cta_type === "ver_categoria";
  return (
    <ModalShell title={story ? "Editar story" : "Novo story"} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-2">
          {["image", "video"].map((t) => <button key={t} onClick={() => set("media_type", t)} data-testid={`se-type-${t}`} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${f.media_type === t ? "bg-[#FF6B00]" : "bg-white/5 text-zinc-400"}`}>{t === "image" ? "Imagem" : "Vídeo"}</button>)}
        </div>
        <Uploader value={f.media} onChange={(v) => set("media", v)} kind={f.media_type} accept={f.media_type === "video" ? "video/*" : "image/*"} label="Mídia" tid="se-media" />
        <Uploader value={f.thumbnail} onChange={(v) => set("thumbnail", v)} label="Thumbnail (capa do story)" tid="se-thumb" />
        <Field label="Título" value={f.title} onChange={(v) => set("title", v)} tid="se-title" />
        <TextArea label="Texto curto" value={f.text} onChange={(v) => set("text", v)} rows={2} tid="se-text" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ordem" type="number" value={f.order} onChange={(v) => set("order", v)} tid="se-order" />
          <div className="flex items-end"><div className="flex items-center justify-between w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Ativo</span><Toggle checked={f.active} onChange={(v) => set("active", v)} tid="se-active" /></div></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Início" type="date" value={(f.start_at || "").slice(0, 10)} onChange={(v) => set("start_at", v)} tid="se-start" />
          <Field label="Expiração" type="date" value={(f.expire_at || "").slice(0, 10)} onChange={(v) => set("expire_at", v)} tid="se-expire" />
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-400">CTA</label>
          <select value={f.cta_type} onChange={(e) => set("cta_type", e.target.value)} data-testid="se-cta" className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 outline-none text-sm">
            <option value="nenhum" className="bg-zinc-900">Nenhum</option>
            <option value="ver_produto" className="bg-zinc-900">Ver produto</option>
            <option value="ver_categoria" className="bg-zinc-900">Ver categoria</option>
            <option value="adicionar" className="bg-zinc-900">Adicionar ao pedido</option>
            <option value="whatsapp" className="bg-zinc-900">WhatsApp</option>
          </select>
        </div>
        {needsProduct && <SelectRef label="Produto" items={data.products} value={f.cta_value} onChange={(v) => set("cta_value", v)} tid="se-ctaproduct" />}
        {needsCategory && <SelectRef label="Categoria" items={data.categories} value={f.cta_value} onChange={(v) => set("cta_value", v)} tid="se-ctacat" />}
      </div>
      <SaveRow onClose={onClose} onSave={save} loading={loading} tid="se-save" />
    </ModalShell>
  );
}

// ---------- Videos ----------
function Videos({ rid, data, reload }) {
  const [ed, setEd] = useState(null);
  const del = async (v) => { if (!window.confirm("Excluir vídeo?")) return; try { await api.delete(`/restaurants/${rid}/videos/${v.id}`); reload(); toast.success("Excluído"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setEd({ new: true })} data-testid="video-add" className="flex items-center gap-2 rounded-full bg-[#FF6B00] px-4 py-2.5 font-bold orange-glow"><Plus size={18} /> Novo vídeo</button></div>
      {data.videos.length === 0 && <Empty>Nenhum vídeo ainda.</Empty>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {data.videos.map((v) => (
          <Card key={v.id} className="!p-0 overflow-hidden">
            <div className="h-40"><SmartImage src={v.thumbnail} className="h-full w-full" /></div>
            <div className="p-3">
              <p className="font-semibold text-sm truncate">{v.title || "Sem título"}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEd(v)} data-testid={`video-edit-${v.id}`} className="flex-1 text-xs font-semibold bg-white/10 py-1.5 rounded-lg flex items-center justify-center gap-1"><Pencil size={13} /> Editar</button>
                <button onClick={() => del(v)} data-testid={`video-del-${v.id}`} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-rose-600 flex items-center justify-center"><Trash2 size={14} /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {ed && <VideoEditor rid={rid} video={ed.new ? null : ed} data={data} onClose={() => setEd(null)} onSaved={() => { setEd(null); reload(); }} />}
    </div>
  );
}
function VideoEditor({ rid, video, data, onClose, onSaved }) {
  const [f, setF] = useState(video || { video: null, thumbnail: null, title: "", product_id: null, active: true, order: data.videos.length, duration: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      if (video) await api.put(`/restaurants/${rid}/videos/${video.id}`, payload);
      else await api.post(`/restaurants/${rid}/videos`, payload);
      toast.success("Vídeo salvo"); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <ModalShell title={video ? "Editar vídeo" : "Novo vídeo"} onClose={onClose}>
      <div className="space-y-3">
        <Uploader value={f.video} onChange={(v) => set("video", v)} kind="video" accept="video/*" label="Vídeo (galeria)" tid="ve-video" />
        <Uploader value={f.thumbnail} onChange={(v) => set("thumbnail", v)} label="Thumbnail" tid="ve-thumb" />
        <Field label="Título" value={f.title} onChange={(v) => set("title", v)} tid="ve-title" />
        <div className="grid grid-cols-2 gap-3">
          <SelectRef label="Produto relacionado" items={data.products} value={f.product_id} onChange={(v) => set("product_id", v)} tid="ve-product" allowEmpty />
          <Field label="Duração" value={f.duration} onChange={(v) => set("duration", v)} tid="ve-duration" placeholder="0:32" />
        </div>
        <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5"><span className="text-sm">Ativo</span><Toggle checked={f.active} onChange={(v) => set("active", v)} tid="ve-active" /></div>
      </div>
      <SaveRow onClose={onClose} onSave={save} loading={loading} tid="ve-save" />
    </ModalShell>
  );
}

// ---------- Best Sellers ----------
function BestSellers({ rid, data, reload }) {
  const best = data.products.filter((p) => p.is_best_seller).sort((a, b) => a.best_seller_order - b.best_seller_order);
  const rest = data.products.filter((p) => !p.is_best_seller);
  const setBest = async (p, val, order) => { try { await api.put(`/restaurants/${rid}/products/${p.id}`, { is_best_seller: val, best_seller_order: order }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const move = async (idx, dir) => {
    const arr = [...best]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await Promise.all(arr.map((p, i) => api.put(`/restaurants/${rid}/products/${p.id}`, { best_seller_order: i })));
    reload();
  };
  return (
    <div className="space-y-4">
      <Card title="Selecionados" desc="Aparecem no carrossel 'Mais Vendidos'.">
        {best.length === 0 && <p className="text-sm text-zinc-500">Nenhum selecionado.</p>}
        <div className="space-y-2">
          {best.map((p, idx) => (
            <div key={p.id} className="flex items-center gap-3">
              <div className="flex flex-col"><button onClick={() => move(idx, -1)} data-testid={`best-up-${p.id}`} className="text-zinc-500 hover:text-white"><ChevronUp size={15} /></button><button onClick={() => move(idx, 1)} data-testid={`best-down-${p.id}`} className="text-zinc-500 hover:text-white"><ChevronDown size={15} /></button></div>
              <SmartImage src={p.image} className="h-10 w-10 rounded-lg" />
              <span className="flex-1 text-sm truncate">{p.name}</span>
              <button onClick={() => setBest(p, false, 0)} data-testid={`best-remove-${p.id}`} className="text-xs text-rose-400 font-semibold">Remover</button>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Adicionar produtos">
        <div className="space-y-1 max-h-72 overflow-y-auto no-scrollbar">
          {rest.map((p) => (
            <button key={p.id} onClick={() => setBest(p, true, best.length)} data-testid={`best-add-${p.id}`} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
              <SmartImage src={p.image} className="h-9 w-9 rounded-lg" /><span className="flex-1 text-sm truncate">{p.name}</span><Plus size={16} className="text-[#FF6B00]" />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------- Offers ----------
function Offers({ rid, data, reload }) {
  const toggle = async (p) => { try { await api.put(`/restaurants/${rid}/products/${p.id}`, { on_offer: !p.on_offer }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card title="Ofertas Especiais" desc="Produtos com preço promocional. Ative para exibir na seção de ofertas.">
      <div className="space-y-2">
        {data.products.map((p) => {
          const pct = discountPct(p.price, p.promo_price);
          return (
            <div key={p.id} className="flex items-center gap-3">
              <SmartImage src={p.image} className="h-10 w-10 rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-xs text-zinc-500">{p.promo_price ? <><span className="line-through mr-1">{brl(p.price)}</span><span className="text-[#FF6B00]">{brl(p.promo_price)}</span> · -{pct}%</> : <span className="text-amber-400">Sem preço promocional</span>}</p>
              </div>
              <Toggle checked={p.on_offer && !!p.promo_price} onChange={() => p.promo_price ? toggle(p) : toast.error("Defina um preço promocional no produto")} tid={`offer-toggle-${p.id}`} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- shared ----------
function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-3 py-6 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-md bg-[#141417] border border-white/10 rounded-3xl p-6 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-head text-lg font-extrabold">{title}</h3><button onClick={onClose}><X size={20} className="text-zinc-500" /></button></div>
        <div className="max-h-[70vh] overflow-y-auto no-scrollbar pr-1">{children}</div>
      </div>
    </div>
  );
}
function SaveRow({ onClose, onSave, loading, tid }) {
  return <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/10"><button onClick={onClose} className="rounded-full bg-white/10 px-5 py-2.5 font-semibold">Cancelar</button><SaveBtn onClick={onSave} loading={loading} tid={tid} /></div>;
}
function SelectRef({ label, items, value, onChange, tid, allowEmpty }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-400">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={tid} className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 outline-none text-sm">
        {allowEmpty && <option value="" className="bg-zinc-900">Nenhum</option>}
        {!allowEmpty && !value && <option value="" className="bg-zinc-900">Selecione...</option>}
        {items.map((it) => <option key={it.id} value={it.id} className="bg-zinc-900">{it.name}</option>)}
      </select>
    </div>
  );
}
